// src/store/features/billing/billingSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiFetch } from '../../../lib/api';
import {
  fetchBillingInvoices,
  fetchBillingPaymentMethods,
  fetchBillingPrices,
  fetchBillingSubscription,
  invalidateBillingCache,
  type BillingFetchOptions,
} from '../../../lib/clientBilling';
import type {
  BillingState,
  PaymentMethod,
  Invoice,
  CreateSubResponse,
  PriceInfo,
  SubscriptionSummary,
  SwitchSubResponse,
  CreateSubscriptionArgs,    
  SwitchSubscriptionArgs,
} from './types';

/* ───────────── initial state ───────────── */
const initialState: BillingState = {
  loading: false,
  error: null,

  prices: {},

  paymentMethods: [],
  invoices: [],

  setupClientSecret: null,

  creatingSub: false,
  subStatus: null,
  subId: null,
  subClientSecret: null,

  subscription: null,

  switchingSub: false,
  cancelingSub: false,
  resumingSub: false,
};

/* ───────────── thunks talking to your backend ───────────── */

/** Server-approved prices for each plan (defense-in-depth vs forged priceIds) */
export const fetchPrices = createAsyncThunk(
  'billing/fetchPrices',
  async (options?: BillingFetchOptions) => {
    return fetchBillingPrices(options ?? {});
  }
);

export const fetchPaymentMethods = createAsyncThunk(
  'billing/fetchPaymentMethods',
  async (options?: BillingFetchOptions) => {
    return fetchBillingPaymentMethods(options ?? {});
  }
);

export const createSetupIntent = createAsyncThunk(
  'billing/createSetupIntent',
  async () => {
    const res = await apiFetch<{ clientSecret: string }>(
      '/api/billing/setup-intent',
      { method: 'POST' }
    );
    return res.clientSecret;
  }
);

export const setDefaultPaymentMethod = createAsyncThunk(
  'billing/setDefaultPaymentMethod',
  async (pmId: string, { dispatch }) => {
    await apiFetch(`/api/billing/payment-methods/${pmId}/default`, {
      method: 'POST',
    });
    // refresh list so UI reflects the new default
    invalidateBillingCache('paymentMethods');
    await dispatch(fetchPaymentMethods({ force: true }));
    return true;
  }
);

export const removePaymentMethod = createAsyncThunk(
  'billing/removePaymentMethod',
  async (pmId: string, { dispatch }) => {
    await apiFetch(`/api/billing/payment-methods/${pmId}`, {
      method: 'DELETE',
    });
    // refresh list so UI reflects removal
    invalidateBillingCache('paymentMethods');
    await dispatch(fetchPaymentMethods({ force: true }));
    return true;
  }
);

export const fetchInvoices = createAsyncThunk(
  'billing/fetchInvoices',
  async (options?: BillingFetchOptions) => {
    return fetchBillingInvoices(options ?? {});
  }
);

export const createSubscription = createAsyncThunk<
  CreateSubResponse,
  CreateSubscriptionArgs | string
>('billing/createSubscription', async (input) => {
  const body: CreateSubscriptionArgs =
    typeof input === 'string' ? { priceId: input } : (input ?? {});
  const res = await apiFetch<CreateSubResponse>(
    '/api/billing/create-subscription',
    { method: 'POST', body: JSON.stringify(body) }
  );
  invalidateBillingCache('subscription');
  return res;
});

/** Current subscription snapshot */
export const getSubscription = createAsyncThunk(
  'billing/getSubscription',
  async (options?: BillingFetchOptions) => {
    return fetchBillingSubscription(options ?? {});
  }
);

// Thunk: switchSubscription – use SwitchSubResponse
export const switchSubscription = createAsyncThunk<
  SwitchSubResponse,
  SwitchSubscriptionArgs
>('billing/switchSubscription', async (input) => {
  const res = await apiFetch<SwitchSubResponse>(
    '/api/billing/switch-subscription',
    { method: 'POST', body: JSON.stringify(input ?? {}) }
  );
  invalidateBillingCache('subscription');
  return res;
});


/** Cancel at period end */
export const cancelSubscription = createAsyncThunk(
  'billing/cancelSubscription',
  async () => {
    const res = await apiFetch<{ ok: true; status: string; cancelAt?: number | null }>(
      '/api/billing/cancel-subscription',
      { method: 'POST' }
    );
    invalidateBillingCache('subscription');
    return res;
  }
);

/** Resume (remove cancel_at_period_end) */
export const resumeSubscription = createAsyncThunk(
  'billing/resumeSubscription',
  async () => {
    const res = await apiFetch<{ ok: true; status: string }>(
      '/api/billing/resume-subscription',
      { method: 'POST' }
    );
    invalidateBillingCache('subscription');
    return res;
  }
);

/** Returns a URL; caller should window.location.assign(url) (no body; backend uses env/default) */
export const createBillingPortalSession = createAsyncThunk(
  'billing/createBillingPortalSession',
  async () => {
    const res = await apiFetch<{ url: string }>(
      '/api/billing/portal',
      { method: 'POST' }
    );
    return res.url;
  }
);

/* ───────────── slice ───────────── */

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearSetupIntent(state) {
      state.setupClientSecret = null;
    },
    clearSubClientSecret(state) {
      state.subClientSecret = null;
    },
    resetErrors(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // prices
      .addCase(fetchPrices.pending, (s) => {
        s.loading = Object.keys(s.prices).length === 0;
        s.error = null;
      })
      .addCase(fetchPrices.fulfilled, (s, a: PayloadAction<PriceInfo[]>) => {
        s.loading = false;
        const map = { ...s.prices };
        for (const p of a.payload) map[p.planId] = p;
        s.prices = map;
      })
      .addCase(fetchPrices.rejected, (s, a) => {
        s.loading = false; s.error = a.error.message || 'Failed to load prices';
      })

      // payment methods
      .addCase(fetchPaymentMethods.pending, (s) => {
        s.loading = s.paymentMethods.length === 0;
        s.error = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (s, a: PayloadAction<PaymentMethod[]>) => {
        s.loading = false; s.paymentMethods = a.payload;
      })
      .addCase(fetchPaymentMethods.rejected, (s, a) => {
        s.loading = false; s.error = a.error.message || 'Failed to load payment methods';
      })

      // setup intent
      .addCase(createSetupIntent.pending, (s) => {
        s.loading = true; s.error = null; s.setupClientSecret = null;
      })
      .addCase(createSetupIntent.fulfilled, (s, a: PayloadAction<string>) => {
        s.loading = false; s.setupClientSecret = a.payload;
      })
      .addCase(createSetupIntent.rejected, (s, a) => {
        s.loading = false; s.error = a.error.message || 'Failed to create setup intent';
      })

      // set/remove PM (state refreshes via fetchPaymentMethods)
      .addCase(setDefaultPaymentMethod.rejected, (s, a) => {
        s.error = a.error.message || 'Failed to set default payment method';
      })
      .addCase(removePaymentMethod.rejected, (s, a) => {
        s.error = a.error.message || 'Failed to remove payment method';
      })

      // invoices
      .addCase(fetchInvoices.pending, (s) => {
        s.loading = s.invoices.length === 0;
        s.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (s, a: PayloadAction<Invoice[]>) => {
        s.loading = false; s.invoices = a.payload;
      })
      .addCase(fetchInvoices.rejected, (s, a) => {
        s.loading = false; s.error = a.error.message || 'Failed to load invoices';
      })

      // subscription (create)
      .addCase(createSubscription.pending, (s) => {
        s.creatingSub = true; s.error = null; s.subClientSecret = null;
      })
      .addCase(createSubscription.fulfilled, (s, a: PayloadAction<CreateSubResponse>) => {
        s.creatingSub = false;
        s.subStatus = a.payload.status;
        s.subId = a.payload.subscriptionId;
        s.subClientSecret = a.payload.clientSecret || null; // present if requires_action
      })
      .addCase(createSubscription.rejected, (s, a) => {
        s.creatingSub = false; s.error = a.error.message || 'Failed to create subscription';
      })

      // subscription (get)
      .addCase(getSubscription.pending, (s) => {
        s.loading = s.subscription == null;
        s.error = null;
      })
      .addCase(getSubscription.fulfilled, (s, a: PayloadAction<SubscriptionSummary | null>) => {
        s.loading = false;
        s.subscription = a.payload;
        if (a.payload) {
          s.subId = a.payload.id ?? s.subId;
          s.subStatus = a.payload.status ?? s.subStatus;
        }
      })
      .addCase(getSubscription.rejected, (s, a) => {
        s.loading = false; s.error = a.error.message || 'Failed to fetch subscription';
      })

      // switch plan
      .addCase(switchSubscription.pending, (s) => {
        s.switchingSub = true; s.error = null;
        s.subClientSecret = null;                 // ← clear any stale secret before a new switch
      })
      .addCase(switchSubscription.fulfilled, (s, a: PayloadAction<SwitchSubResponse>) => {
        s.switchingSub = false;
        if (s.subscription) s.subscription.status = a.payload.status;
        s.subStatus = a.payload.status;
        if (a.payload.clientSecret) s.subClientSecret = a.payload.clientSecret; // ← store secret when SCA needed
      })
      .addCase(switchSubscription.rejected, (s, a) => {
        s.switchingSub = false; s.error = a.error.message || 'Failed to switch plan';
      })


      // cancel
      .addCase(cancelSubscription.pending, (s) => { s.cancelingSub = true; s.error = null; })
      .addCase(cancelSubscription.fulfilled, (s, a: PayloadAction<{ ok: true; status: string; cancelAt?: number | null }>) => {
        s.cancelingSub = false;
        const { status, cancelAt } = a.payload;
        if (s.subscription) {
          s.subscription.status = status;
          s.subscription.cancel_at = cancelAt ?? null;
        }
        s.subStatus = status;
      })
      .addCase(cancelSubscription.rejected, (s, a) => {
        s.cancelingSub = false; s.error = a.error.message || 'Failed to cancel subscription';
      })

      // resume
      .addCase(resumeSubscription.pending, (s) => { s.resumingSub = true; s.error = null; })
      .addCase(resumeSubscription.fulfilled, (s, a: PayloadAction<{ ok: true; status: string }>) => {
        s.resumingSub = false;
        const { status } = a.payload;
        if (s.subscription) {
          s.subscription.status = status;
          s.subscription.cancel_at = null;
        }
        s.subStatus = status;
      })
      .addCase(resumeSubscription.rejected, (s, a) => {
        s.resumingSub = false; s.error = a.error.message || 'Failed to resume subscription';
      })

      // portal
      .addCase(createBillingPortalSession.rejected, (s, a) => {
        s.error = a.error.message || 'Failed to open billing portal';
      });
  },
});

export const { clearSetupIntent, clearSubClientSecret, resetErrors } = billingSlice.actions;
export default billingSlice.reducer;
